import React from 'react';
import { useLocation } from 'react-router-dom';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import { useDocumentTabs } from '../context/DocumentTabsContext';
import { isBrandedPreset } from '../theme';

const TABS_PER_ROW = 8;

function TabIcon({ kind }) {
  if (kind === 'quote') return <DescriptionIcon sx={{ fontSize: '0.95rem', opacity: 0.9 }} />;
  if (kind === 'order') return <AssignmentIcon sx={{ fontSize: '0.95rem', opacity: 0.9 }} />;
  return <ShoppingBagIcon sx={{ fontSize: '0.95rem', opacity: 0.9 }} />;
}

function chunkTabs(list, size) {
  const rows = [];
  for (let i = 0; i < list.length; i += size) {
    rows.push(list.slice(i, i + size));
  }
  return rows;
}

export default function DocumentTabBar() {
  const location = useLocation();
  const { tabs, tryNavigate, tryCloseTab } = useDocumentTabs();

  if (!tabs.length) return null;

  const tabRows = chunkTabs(tabs, TABS_PER_ROW);

  return (
    <Box
      className="no-print"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        alignSelf: 'stretch',
        gap: 0.25,
        mb: 1,
        minHeight: 28,
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 0,
        width: '100%'
      }}
    >
      {tabRows.map((rowTabs, rowIndex) => (
        <Stack
          key={rowIndex}
          direction="row"
          spacing={0.25}
          flexWrap="wrap"
          useFlexGap
          sx={{
            alignItems: 'flex-end',
            alignContent: 'flex-start',
            justifyContent: 'flex-start',
            rowGap: 0.25
          }}
        >
          {rowTabs.map((tab) => {
            const active = tab.path === location.pathname;
            const flushWithContent = active && rowIndex === tabRows.length - 1;
            return (
              <Box
                key={tab.path}
                onClick={() => tryNavigate(tab.path)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.25,
                  px: 0.75,
                  py: 0.25,
                  minHeight: 26,
                  maxWidth: 220,
                  cursor: 'pointer',
                  borderTopLeftRadius: 6,
                  borderTopRightRadius: 6,
                  border: '1px solid',
                  borderBottom: flushWithContent ? '1px solid transparent' : undefined,
                  mb: flushWithContent ? '-1px' : 0,
                  bgcolor: active ? 'background.paper' : 'primary.light',
                  color: active
                    ? 'primary.main'
                    : (theme) =>
                        isBrandedPreset(theme.fw?.preset)
                          ? theme.palette.primary.dark
                          : theme.palette.primary.contrastText,
                  borderColor: active ? 'divider' : 'primary.light',
                  flexShrink: 0,
                  userSelect: 'none',
                  '&:hover': {
                    bgcolor: active ? 'background.paper' : 'primary.main',
                    color: active ? 'primary.main' : 'primary.contrastText',
                    borderColor: active ? 'divider' : 'primary.main'
                  }
                }}
              >
                <TabIcon kind={tab.kind} />
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: active ? 600 : 500,
                    lineHeight: 1.2,
                    flex: 1,
                    minWidth: 0
                  }}
                >
                  {tab.title}
                </Typography>
                <IconButton
                  component="span"
                  size="small"
                  aria-label="Close tab"
                  onClick={(e) => {
                    e.stopPropagation();
                    tryCloseTab(tab.path);
                  }}
                  sx={{
                    p: 0.125,
                    ml: 0.125,
                    color: 'inherit',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <CloseIcon sx={{ fontSize: '0.85rem' }} />
                </IconButton>
              </Box>
            );
          })}
        </Stack>
      ))}
    </Box>
  );
}
